import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import BatteryFormFields from 'components/forms/BatteryFormFields';

import useBattery from 'hooks/useBattery';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props, UpdateBatteryFormSchema } from './types';

import styles from './styles.module.scss';

const UpdateBatteryForm: FunctionComponent<Props> = ({
    className = '',
    formValues,
    isClosing,
    onClose,
    setIsClosing,
    testId = TEST_ID
}) => {
    const { updateBattery } = useBattery();
    const { SAVE } = useTranslation();

    const form = useForm<UpdateBatteryFormSchema>({
        defaultValues: formValues,
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose: () => setIsClosing?.(false),
        onClose,
        requestingClose: isClosing
    });

    const onSubmit = async ({ id, ...data }: UpdateBatteryFormSchema) => {
        if (!formValues) {
            return;
        }

        const battery = await updateBattery({
            battery: { ...data, status: formValues.status },
            id
        });

        if (battery) {
            onClose?.();
        }
    };

    return (
        <VerticalContainer className={`${styles.updateBatteryForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form className={styles.form} form={form}>
                    <BatteryFormFields />

                    <FormSubmitPanel
                        buttonText={SAVE}
                        className={styles.formSubmitPanel}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default UpdateBatteryForm;
